"use client";

import { Zap, Crown, Sparkles, ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const steps = [{
  number: "01",
  title: "ANALISI",
  description: "Un audit della tua comunicazione. Capisco dove sei, dove vuoi andare, cosa si mette in mezzo. Cosa ti distingue dagli altri?",
  Icon: Zap
}, {
  number: "02",
  title: "STRATEGIA E METODO",
  description: "Costruiamo insieme il percorso più diretto. Nessun pacchetto standard - solo quello che serve davvero.",
  Icon: Crown
}, {
  number: "03",
  title: "CREATIVITÀ",
  description: "Comunicazione più chiara, brand più forte e autentico. Dati misurabili.",
  Icon: Sparkles
}];
export default function Roadmap() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="3883" id="the-roadmap" className="bg-background text-foreground relative overflow-hidden py-32 md:py-[200px]">
      <div className="pointer-events-none absolute top-0 left-4 hidden h-full lg:left-8 lg:block">
        <div className="sticky top-32">
          <div className="font-default text-[10px] font-medium tracking-[0.3em] uppercase text-foreground/50" style={{
          writingMode: "vertical-rl"
        }}>
            00 &mdash; THE PROCESS
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-20">
        <div ref={header.ref} className={`grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-12 transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="md:col-span-7">
            <div className="mb-6 flex items-center gap-3">
              <span className="h-px w-10 bg-primary" />
              <span className="font-default text-xs font-medium tracking-[0.25em] uppercase text-primary">
                Flusso di Lavoro Strategico
              </span>
            </div>
            <h2 className="font-default font-bold text-4xl leading-[1.05] tracking-tight text-foreground sm:text-5xl md:text-6xl lg:text-[64px]">Come funziona lavorare con  noi?</h2>
          </div>
          <div className="md:col-span-5 md:pt-6">
            <p className="font-default text-lg leading-relaxed text-foreground/75 md:text-xl">
              Ogni progetto segue un percorso rigoroso e mirato. Operiamo attraverso cicli focalizzati, assicurando che ogni elemento visivo e strategico serva a un obiettivo preciso per l'evoluzione del tuo brand.
            </p>
            <p className="mt-5 font-default text-base leading-relaxed text-foreground/60">
              Nessuna scorciatoia. Solo una coreografia deliberata tra strategia, design e visione autentica.
            </p>
          </div>
        </div>

        <div className="relative mt-20 md:mt-28">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-5">
            {steps.map((step, i) => {
            const card = useRevealOnScroll<HTMLDivElement>({
              threshold: 0.15
            });
            const Icon = step.Icon;
            return <div key={step.number} data-index={i} ref={card.ref} className={`group relative transition-all duration-700 ease-out ${card.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
              transitionDelay: `${i * 120}ms`
            }}>
                  {i < steps.length - 1 && <div className="pointer-events-none absolute top-1/2 -right-6 z-20 hidden lg:block">
                      <div className="flex items-center text-primary">
                        <ArrowRight className="size-8" strokeWidth={1.5} />
                      </div>
                    </div>}

                  <div className="relative h-[380px] overflow-hidden border border-border/50 bg-card backdrop-blur-xl shadow-sm transition-all duration-500 group-hover:-translate-y-1 group-hover:border-primary group-hover:shadow-lg rounded-lg">
                    <div className="absolute -top-4 -left-2 select-none">
                      <span className="font-default text-[120px] font-extrabold leading-none tracking-tighter text-foreground/10 transition-colors duration-[400ms] group-hover:text-primary" aria-hidden="true">
                        {step.number}
                      </span>
                    </div>

                    <div className="relative flex h-full flex-col justify-between p-8 pt-32">
                      <Icon className="h-10 w-10 text-foreground/80 transition-colors duration-300 group-hover:text-primary" strokeWidth={1} />

                      <div>
                        <div className="mb-4 h-px w-8 bg-foreground/30 transition-all duration-500 group-hover:w-16 group-hover:bg-primary" />
                        <h3 className="font-default font-bold text-2xl tracking-[0.15em] uppercase text-foreground">
                          {step.title}
                        </h3>
                        <p className="mt-4 font-default text-[15px] leading-relaxed text-foreground/65">
                          {step.description}
                        </p>
                      </div>
                    </div>

                    <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary to-transparent" />
                    </div>
                  </div>
                </div>;
          })}
          </div>

          <div className="mt-16 flex justify-center">
            <Button asChild size="lg" className="h-14 px-10 rounded-lg font-bold tracking-wider uppercase bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="#">Inizia da qui</Link>
            </Button>
          </div>

          <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-border pt-8 sm:flex-row sm:items-center">
            <p className="font-default text-xs tracking-[0.25em] uppercase text-foreground/50">
              ANALISI, STRATEGIA E METODO, CREATIVITÀ
            </p>
            <p className="font-default italic text-sm text-foreground/60">
              Executed with absolute focus. Always.
            </p>
          </div>
        </div>
      </div>
    </section>;
}